import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { AIChatHistoryService } from '../../services/ai-chat-history.service';
import { AIChatboxService } from '../../services/ai-chatbox.service';
import { ChatHistoryItem } from '../../models/ai-chat-history.model';

function relativeTime(dateStr: string): string {
	const diffMs = Date.now() - new Date(dateStr).getTime()
	const diffMin = Math.floor(diffMs / 60000)
	if (diffMin < 60) return `${diffMin}m`
	const diffH = Math.floor(diffMin / 60)
	if (diffH < 24) return `${diffH}h`
	return `${Math.floor(diffH / 24)}j`
}

@Component({
	selector: 'ai-chat-conversation-history',
	template: `
		@if (insight()) {
			<div class="history-row d-flex align-items-center p-2">
				<span class="mr-1 ml-4" translate="App.AIChat.ConversationHistory"></span>
				<button
					class="btn btn-link no-arrow mx-2 p-0"
					[ngbTooltip]="'App.AIChat.ConversationHistory' | translate"
					placement="bottom"
					container="body"
					[openDelay]="500"
					(click)="toggleOpen()">
					<i class="far fa-clock"></i>
				</button>
			</div>

			@if (open()) {
				<div class="history-search px-2 pb-1">
					<div class="input-group input-group-sm">
						<div class="input-group-prepend">
							<span class="input-group-text"><i class="far fa-search"></i></span>
						</div>
						<input
							type="text"
							class="form-control"
							[placeholder]="'App.AIChat.SearchConversation' | translate"
							[ngModel]="searchText()"
							(ngModelChange)="searchText.set($event)"
						/>
					</div>
				</div>

				@if (filteredConversations().length > 0) {
					<ul class="history-dropdown list-unstyled mb-0">
						@for (conv of filteredConversations(); track conv.threadId) {
							<li
								class="history-item d-flex align-items-center px-3 py-1"
								(mouseenter)="hoveredThreadId.set(conv.threadId)"
								(mouseleave)="hoveredThreadId.set(null)"
								(click)="selectConversation(conv)">
								<span class="history-item-title text-truncate flex-grow-1 mr-2">{{ conv.firstQuestion }}</span>
								@if (hoveredThreadId() === conv.threadId) {
									<button
										class="btn btn-link btn-sm p-0 mr-1"
										[ngbTooltip]="'App.AIChat.ResumeConversation' | translate"
										container="body"
										(click)="selectConversation(conv); $event.stopPropagation()">
										<i class="far fa-pencil"></i>
									</button>
									<button
										class="btn btn-link btn-sm p-0 text-danger"
										[ngbTooltip]="'App.AIChat.DeleteConversation' | translate"
										container="body"
										(click)="deleteConversation(conv, $event)">
										<i class="far fa-trash-alt"></i>
									</button>
								} @else {
									<span class="history-item-age text-muted small">{{ relativeTime(conv.lastChatDate) }}</span>
								}
							</li>
						}
					</ul>
				} @else if (!loading()) {
					<p class="text-muted small px-3 py-1" translate="App.AIChat.NoConversations"></p>
				}

				@if (loading()) {
					<p class="text-muted small px-3 py-1"><i class="far fa-spinner fa-spin"></i></p>
				}
			}
		}
	`,
	styles: `
		.history-row {
			background: var(--body-bg);
			border-top: 1px solid #e0e0e0;
		}
		.history-row .btn-link {
			color: inherit;
			opacity: .7;
		}
		.history-row .btn-link:hover {
			opacity: 1;
		}
		.history-dropdown {
			max-height: 220px;
			overflow-y: auto;
			border-top: 1px solid #e0e0e0;
		}
		.history-item {
			cursor: pointer;
			border-bottom: 1px solid #f0f0f0;
			min-height: 32px;
		}
		.history-item:hover {
			background: rgba(0,0,0,.04);
		}
		.history-item-title {
			font-size: 0.85em;
			min-width: 0;
		}
		.history-item-age {
			white-space: nowrap;
			font-size: 0.8em;
		}
	`,
	imports: [CommonModule, TranslateModule, NgbTooltipModule, FormsModule],
})
export class AIConversationHistoryComponent {
	private historyService = inject(AIChatHistoryService)
	private ais = inject(AIChatboxService)

	insight = input(false)

	open = signal(false)
	loading = signal(false)
	searchText = signal('')
	conversations = signal<ChatHistoryItem[]>([])
	hoveredThreadId = signal<string | null>(null)

	filteredConversations = computed(() => {
		const q = this.searchText().toLowerCase()
		return q
			? this.conversations().filter(c => c.firstQuestion.toLowerCase().includes(q))
			: this.conversations()
	})

	relativeTime = relativeTime

	toggleOpen() {
		const next = !this.open()
		this.open.set(next)
		if (next && this.conversations().length === 0) {
			this.loadHistory()
		}
	}

	private loadHistory() {
		this.loading.set(true)
		this.historyService.getHistory().subscribe({
			next: list => this.conversations.set(list),
			complete: () => this.loading.set(false),
			error: () => this.loading.set(false),
		})
	}

	selectConversation(conv: ChatHistoryItem) {
		this.open.set(false)
		this.historyService.getMessages(conv.threadId).subscribe(messages => {
			if (messages.length === 0) return
			const last = messages[messages.length - 1]
			this.ais.requestResume({
				threadId: last.threadId,
				contextId: last.contextId,
				messages,
			})
		})
	}

	deleteConversation(conv: ChatHistoryItem, event: Event) {
		event.stopPropagation()
		this.historyService.deleteThread(conv.threadId).subscribe(() => {
			this.conversations.update(list => list.filter(c => c.threadId !== conv.threadId))
		})
	}
}
